import React from "react";
import { Bar } from "react-chartjs-2";
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from "chart.js";
import { useApiData } from "../hooks/useApiData";
import { unwrapArray } from "../utils/api";
import { formatNumber, getChartColors, tooltipStyle } from "./charts/chartTheme";
import LoadingOverlay from "./ui/LoadingOverlay";
import { ChartSkeleton } from "./ui/Skeleton";
import { EmptyState, ErrorState } from "./ui/StateBlock";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// 412000 -> "412k", 1250000 -> "1,3M" (short axis labels, so they do not eat width on a phone)
function compact(v) {
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toLocaleString("sr-RS", { maximumFractionDigits: 1 })}M`;
  if (abs >= 1e3) return `${(v / 1e3).toLocaleString("sr-RS", { maximumFractionDigits: 0 })}k`;
  return String(v);
}

/**
 * Always shows revenue PER HOUR (summed over the whole selected period), so the strongest
 * hour is visible for a week or a month too, not just for a single day. The data usually comes
 * from the parent (hourlyRows); if it is missing, the chart fetches it itself.
 */
export default function RevenueChart({
  startDate,
  endDate,
  warehouse,
  darkMode,
  hourlyRows,
  loading: parentLoading,
  error: parentError,
  onRetry,
}) {
  const colors = getChartColors(darkMode);
  const whParam = warehouse ? `&warehouse=${warehouse}` : "";
  const hasRange = Boolean(startDate && endDate);

  const needsOwnFetch = !hourlyRows;
  const { data: hourlyRaw, loading: ownLoading, error: ownError } = useApiData(
    hasRange && needsOwnFetch ? `/hourly_sales.php?od=${startDate}&do=${endDate}${whParam}` : null
  );
  // If the parent provides the data, the loading state comes from it as well.
  const loading = needsOwnFetch ? ownLoading : Boolean(parentLoading);
  const error = needsOwnFetch ? ownError : parentError;

  const sourceRows = hourlyRows || unwrapArray(hourlyRaw);

  if (sourceRows.length === 0) {
    if (error) return <ErrorState onRetry={onRetry} />;
    if (loading) return <ChartSkeleton height={220} />;
    return <EmptyState title="Nema prometa u izabranom periodu" hint="Probaj drugi period ili magacin." />;
  }

  // Always all 24 hours (hours without revenue = 0), so the time axis is continuous.
  const byHour = new Map(sourceRows.map((r) => [Number(r.sat), r]));
  const rows = Array.from({ length: 24 }, (_, h) => byHour.get(h) || { sat: h, vrednost: 0, ukupno_racuna: 0 });
  const labels = rows.map((r) => `${String(r.sat).padStart(2, "0")}h`);

  const peak = rows.reduce((best, r) => ((Number(r.vrednost) || 0) > (Number(best.vrednost) || 0) ? r : best), rows[0]);
  const hasPeak = (Number(peak.vrednost) || 0) > 0;

  const chartData = {
    labels,
    datasets: [
      {
        label: "Vrednost (RSD)",
        data: rows.map((r) => parseFloat(r.vrednost) || 0),
        backgroundColor: colors.accent,
        borderRadius: 2,
        barPercentage: 0.9,
        categoryPercentage: 0.8,
        yAxisID: "y",
      },
      {
        label: "Broj računa",
        data: rows.map((r) => parseFloat(r.ukupno_racuna) || 0),
        backgroundColor: colors.muted,
        borderRadius: 2,
        barPercentage: 0.9,
        categoryPercentage: 0.8,
        yAxisID: "y1",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "top",
        align: "end",
        labels: { color: colors.text, font: { size: 11 }, boxWidth: 8, boxHeight: 8, usePointStyle: true, pointStyle: "rectRounded" },
      },
      datalabels: { display: false },
      tooltip: {
        ...tooltipStyle(colors),
        callbacks: {
          label: (ctx) =>
            ctx.dataset.yAxisID === "y"
              ? `Vrednost: ${formatNumber(ctx.raw)} RSD`
              : `Broj računa: ${formatNumber(ctx.raw)}`,
        },
      },
    },
    scales: {
      x: {
        border: { display: false },
        ticks: { color: colors.text, maxRotation: 0, autoSkip: true, maxTicksLimit: 12, padding: 4 },
        grid: { display: false },
      },
      y: {
        position: "left",
        border: { display: false },
        ticks: { color: colors.text, padding: 8, callback: (v) => compact(v) },
        grid: { color: colors.grid, drawTicks: false },
      },
      y1: {
        position: "right",
        border: { display: false },
        ticks: { color: colors.text, padding: 8, precision: 0 },
        grid: { drawOnChartArea: false, drawTicks: false },
      },
    },
  };

  return (
    <LoadingOverlay loading={loading}>
      {hasPeak && (
        <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
          Najjači sat:{" "}
          <span className="font-medium tabular-nums text-slate-800 dark:text-slate-100">
            {String(peak.sat).padStart(2, "0")}h
          </span>{" "}
          sa <span className="font-medium tabular-nums text-slate-800 dark:text-slate-100">{formatNumber(peak.vrednost)} RSD</span>
        </p>
      )}
      <div className="relative w-full min-w-0 overflow-hidden" style={{ height: 220 }}>
        <Bar data={chartData} options={options} />
      </div>
    </LoadingOverlay>
  );
}
